import { useLocalization, type SupportedLocale } from "../lib/i18n";

type TermsSection = {
  title: string;
  blocks: Array<
    | { kind: "paragraph"; text: string }
    | { kind: "list"; items: string[] }
    | { kind: "contact"; lines: string[] }
  >;
};

type TermsContent = {
  eyebrow: string;
  title: string;
  updated: string;
  intro: string;
  sections: TermsSection[];
};

const termsContent: Partial<Record<SupportedLocale, TermsContent>> = {
  en: {
    eyebrow: "Sport Exchange 100",
    title: "Terms and Conditions",
    updated: "Effective Date: May 16, 2026 - Last Updated: May 16, 2026",
    intro:
      'These Terms and Conditions ("Terms") govern your use of the Sport Exchange 100 mobile application ("the App"), operated by SportEx100 Technologies Inc. ("we," "us," or "our"). By downloading, installing, or using the App, you agree to be bound by these Terms. If you do not agree, do not use the App.',
    sections: [
      {
        title: "1. Fictional Currency and Simulation Disclaimer",
        blocks: [
          {
            kind: "paragraph",
            text:
              "1.1 No Real Value. Sport Exchange 100 is a free-to-play simulation game. All points, balances, rankings, simulated portfolios, trades, orders, picks, and market prices within the App are entirely fictional. They have no cash value and cannot be deposited, withdrawn, redeemed, transferred for money, or exchanged for prizes of any kind.",
          },
          {
            kind: "paragraph",
            text:
              "1.2 Not Financial or Gambling Activity. Nothing in the App constitutes or represents real assets, contracts, securities, commodities, wagers, or financial instruments. The App does not facilitate gambling, betting, or real-money trading. No real currency, cryptocurrency, or anything of monetary value is staked, wagered, won, or lost through the use of the App.",
          },
          {
            kind: "paragraph",
            text:
              "1.3 Non-Affiliation. Sport Exchange 100 is not affiliated with, endorsed by, or sponsored by any professional sports league, team, or players' association.",
          },
        ],
      },
      {
        title: "2. Accounts and Eligibility",
        blocks: [
          {
            kind: "paragraph",
            text:
              "2.1 Age Requirement. You must be at least 13 years of age, or the minimum age of digital consent in your jurisdiction if higher, to create an account and use the App. If you are under 18, you represent that you have obtained parental or guardian consent to use the App.",
          },
          {
            kind: "paragraph",
            text:
              "2.2 Account Creation. You may create one account per person. Accounts can be created using Sign in with Apple, Google, or email registration. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.",
          },
          {
            kind: "paragraph",
            text:
              "2.3 Accurate Information. You agree to provide accurate and current information during registration. Your trader code or display name must not impersonate another person, contain offensive language, or violate these Terms.",
          },
          {
            kind: "paragraph",
            text:
              "2.4 Account Termination. We reserve the right to suspend or terminate your account at any time, with or without notice, for any reason, including but not limited to violation of these Terms. You may delete your account at any time through the App's settings. Upon deletion, your personal data will be handled in accordance with our Privacy Policy.",
          },
        ],
      },
      {
        title: "3. User Conduct",
        blocks: [
          {
            kind: "paragraph",
            text: "3.1 General Conduct. You agree to use the App in a fair, respectful, and lawful manner. You must not:",
          },
          {
            kind: "list",
            items: [
              "Use the App for any illegal purpose or in violation of any applicable laws.",
              "Harass, threaten, bully, or intimidate other users.",
              "Post or transmit hate speech, discriminatory language, sexually explicit content, or content that promotes violence.",
              "Impersonate another person or misrepresent your identity.",
              "Spam, flood, or disrupt chat or other communication features.",
            ],
          },
          {
            kind: "paragraph",
            text: "3.2 Game Integrity. You must not:",
          },
          {
            kind: "list",
            items: [
              "Create multiple accounts to gain an unfair advantage.",
              "Use bots, scripts, or automated tools to interact with the App.",
              "Exploit bugs, glitches, or vulnerabilities in the App. Please report them to us.",
              "Manipulate the simulated market through coordinated or deceptive trading activity, including wash trading or spoofing.",
              "Collude with other users to artificially influence prices, standings, or outcomes.",
            ],
          },
          {
            kind: "paragraph",
            text:
              "3.3 Chat Content. Chat messages within pools are visible to all pool members. You are solely responsible for the content you post. We reserve the right to remove content and suspend accounts that violate these Terms, without prior notice.",
          },
        ],
      },
      {
        title: "4. Intellectual Property",
        blocks: [
          {
            kind: "paragraph",
            text:
              "4.1 Our Property. The App, including its design, code, graphics, user interface, trade engine, game mechanics, and SportEx100 branding, is owned by or licensed to SportEx100 Technologies Inc. and is protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, reverse-engineer, or create derivative works from the App without our prior written consent.",
          },
          {
            kind: "paragraph",
            text:
              "4.2 Your Content. By posting content in the App, such as chat messages, trader codes, and avatars, you grant us a non-exclusive, royalty-free, worldwide license to use, display, and distribute that content within the App for the purpose of operating the service. You retain ownership of your content. We do not claim ownership of your chat messages or user-generated content.",
          },
          {
            kind: "paragraph",
            text:
              "4.3 Feedback. If you provide us with suggestions, ideas, or feedback about the App, you agree that we may use them freely without any obligation or compensation to you.",
          },
        ],
      },
      {
        title: "5. In-App Purchases",
        blocks: [
          {
            kind: "paragraph",
            text:
              "The App is currently free to use. We may introduce optional in-app purchases in the future to unlock enhanced features or functionality. If and when in-app purchases become available, these Terms will be updated accordingly, and you will be notified in advance as described in Section 7.1.",
          },
        ],
      },
      {
        title: "6. Disclaimers and Limitation of Liability",
        blocks: [
          {
            kind: "paragraph",
            text:
              "6.1 As-Is Basis. THE APP IS PROVIDED ON AN AS IS AND AS AVAILABLE BASIS, WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.",
          },
          {
            kind: "paragraph",
            text:
              "6.2 No Guarantees. We do not guarantee that the App will be uninterrupted, error-free, or free of harmful components. Game data, including portfolios, standings, trade history, and market prices, may be lost, corrupted, or reset at any time due to technical issues, updates, or seasonal resets.",
          },
          {
            kind: "paragraph",
            text:
              "6.3 Limitation of Liability. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, SPORTEX100 TECHNOLOGIES INC. AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, LOSS OF PROFITS, OR LOSS OF GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE APP, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY.",
          },
          {
            kind: "paragraph",
            text:
              "6.4 Maximum Liability. In no event shall our total aggregate liability to you for all claims arising out of or related to the App exceed the greater of (a) the amount you have paid us in the twelve (12) months preceding the claim, or (b) fifty Canadian dollars (CAD $50.00).",
          },
        ],
      },
      {
        title: "7. Modifications and Service Availability",
        blocks: [
          {
            kind: "paragraph",
            text:
              "7.1 Changes to Terms. We may modify these Terms at any time. If we make material changes, we will notify you through the App or by other reasonable means at least fifteen (15) days before the changes take effect. Your continued use of the App after the effective date constitutes acceptance of the updated Terms. If you do not agree to the changes, you must stop using the App and delete your account.",
          },
          {
            kind: "paragraph",
            text:
              "7.2 Changes to the App. We reserve the right to modify, suspend, or discontinue the App, or any feature or content within it, at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuation of the App.",
          },
          {
            kind: "paragraph",
            text:
              "7.3 Seasonal Resets. The App operates on a seasonal basis aligned with professional sports playoffs. At the end of each season, portfolios, standings, trade history, and market data may be reset. We will provide reasonable notice before any seasonal reset.",
          },
          {
            kind: "paragraph",
            text:
              "7.4 Service Availability. We do not guarantee continuous or uninterrupted access to the App. The App may be temporarily unavailable due to maintenance, updates, server issues, or circumstances beyond our control.",
          },
        ],
      },
      {
        title: "8. Governing Law and Dispute Resolution",
        blocks: [
          {
            kind: "paragraph",
            text:
              "8.1 Governing Law. These Terms are governed by and construed in accordance with the laws of the Province of Quebec and the federal laws of Canada applicable therein, without regard to conflict of law principles.",
          },
          {
            kind: "paragraph",
            text:
              "8.2 Dispute Resolution. Any dispute arising out of or relating to these Terms or the App shall first be attempted to be resolved through good-faith negotiation. If the dispute cannot be resolved within thirty (30) days, it shall be submitted to the exclusive jurisdiction of the courts located in the judicial district of Montreal, Quebec, Canada.",
          },
          {
            kind: "paragraph",
            text:
              "8.3 Class Action Waiver. To the fullest extent permitted by applicable law, you agree to resolve disputes with us on an individual basis and waive any right to participate in a class action, collective action, or representative proceeding.",
          },
        ],
      },
      {
        title: "9. General Provisions",
        blocks: [
          {
            kind: "paragraph",
            text:
              "9.1 Entire Agreement. These Terms, together with our Privacy Policy, constitute the entire agreement between you and us regarding the App and supersede all prior agreements and understandings.",
          },
          {
            kind: "paragraph",
            text:
              "9.2 Severability. If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall continue in full force and effect.",
          },
          {
            kind: "paragraph",
            text:
              "9.3 Waiver. Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.",
          },
          {
            kind: "paragraph",
            text:
              "9.4 Assignment. You may not assign or transfer your rights under these Terms without our prior written consent. We may assign our rights and obligations under these Terms without restriction.",
          },
        ],
      },
      {
        title: "10. Contact Us",
        blocks: [
          {
            kind: "paragraph",
            text: "If you have questions about these Terms, contact us at:",
          },
          {
            kind: "contact",
            lines: [
              "SportEx100 Technologies Inc.",
              "Email: info@sportex100.com",
              "Support: https://sportex100.com/support",
            ],
          },
        ],
      },
    ],
  },
  fr: {
    eyebrow: "Sport Exchange 100",
    title: "Conditions générales",
    updated: "Date d’entrée en vigueur : 16 mai 2026 - Dernière mise à jour : 16 mai 2026",
    intro:
      "Les présentes Conditions générales (« Conditions ») régissent votre utilisation de l’application mobile Sport Exchange 100 (l’« Application »), exploitée par SportEx100 Technologies Inc. (« nous », « notre » ou « nos »). En téléchargeant, installant ou utilisant l’Application, vous acceptez d’être lié par les présentes Conditions. Si vous n’êtes pas d’accord, n’utilisez pas l’Application.",
    sections: [
      {
        title: "1. Monnaie fictive et avertissement relatif à la simulation",
        blocks: [
          {
            kind: "paragraph",
            text:
              "1.1 Aucune valeur réelle. Sport Exchange 100 est un jeu de simulation gratuit. Tous les points, soldes, classements, portefeuilles simulés, transactions, ordres, sélections et prix de marché dans l’Application sont entièrement fictifs. Ils n’ont aucune valeur monétaire et ne peuvent pas être déposés, retirés, rachetés, transférés contre de l’argent ni échangés contre des prix de quelque nature que ce soit.",
          },
          {
            kind: "paragraph",
            text:
              "1.2 Aucune activité financière ni de jeu d’argent. Rien dans l’Application ne constitue ni ne représente de vrais actifs, contrats, valeurs mobilières, marchandises, paris ou instruments financiers. L’Application ne facilite pas les jeux d’argent, les paris ni les transactions en argent réel. Aucune devise réelle, cryptomonnaie ou chose ayant une valeur monétaire n’est mise en jeu, pariée, gagnée ou perdue par l’utilisation de l’Application.",
          },
          {
            kind: "paragraph",
            text:
              "1.3 Absence d’affiliation. Sport Exchange 100 n’est affilié à aucune ligue sportive professionnelle, équipe ou association de joueurs, et n’est ni approuvé ni commandité par celles-ci.",
          },
        ],
      },
      {
        title: "2. Comptes et admissibilité",
        blocks: [
          {
            kind: "paragraph",
            text:
              "2.1 Âge requis. Vous devez avoir au moins 13 ans, ou l’âge minimal de consentement numérique applicable dans votre territoire s’il est plus élevé, pour créer un compte et utiliser l’Application. Si vous avez moins de 18 ans, vous déclarez avoir obtenu le consentement d’un parent ou tuteur pour utiliser l’Application.",
          },
          {
            kind: "paragraph",
            text:
              "2.2 Création de compte. Vous pouvez créer un seul compte par personne. Les comptes peuvent être créés au moyen de Connexion avec Apple, Google ou d’une inscription par courriel. Vous êtes responsable de préserver la confidentialité de vos identifiants et de toute activité effectuée sous votre compte.",
          },
          {
            kind: "paragraph",
            text:
              "2.3 Renseignements exacts. Vous acceptez de fournir des renseignements exacts et à jour lors de l’inscription. Votre code de trader ou nom d’affichage ne doit pas usurper l’identité d’une autre personne, contenir du langage offensant ni enfreindre les présentes Conditions.",
          },
          {
            kind: "paragraph",
            text:
              "2.4 Résiliation du compte. Nous nous réservons le droit de suspendre ou résilier votre compte en tout temps, avec ou sans préavis, pour quelque raison que ce soit, y compris en cas de violation des présentes Conditions. Vous pouvez supprimer votre compte en tout temps dans les réglages de l’Application. Après suppression, vos données personnelles seront traitées conformément à notre Politique de confidentialité.",
          },
        ],
      },
      {
        title: "3. Conduite des utilisateurs",
        blocks: [
          {
            kind: "paragraph",
            text: "3.1 Conduite générale. Vous acceptez d’utiliser l’Application de manière équitable, respectueuse et conforme à la loi. Vous ne devez pas :",
          },
          {
            kind: "list",
            items: [
              "Utiliser l’Application à des fins illégales ou en violation des lois applicables.",
              "Harceler, menacer, intimider ou malmener d’autres utilisateurs.",
              "Publier ou transmettre des propos haineux, discriminatoires, sexuellement explicites ou du contenu qui encourage la violence.",
              "Usurper l’identité d’une autre personne ou présenter faussement votre identité.",
              "Envoyer du pourriel, inonder ou perturber le clavardage ou d’autres fonctions de communication.",
            ],
          },
          {
            kind: "paragraph",
            text: "3.2 Intégrité du jeu. Vous ne devez pas :",
          },
          {
            kind: "list",
            items: [
              "Créer plusieurs comptes afin d’obtenir un avantage injuste.",
              "Utiliser des robots, scripts ou outils automatisés pour interagir avec l’Application.",
              "Exploiter des bogues, erreurs ou vulnérabilités de l’Application. Veuillez nous les signaler.",
              "Manipuler le marché simulé par une activité coordonnée ou trompeuse, y compris les transactions fictives ou le spoofing.",
              "Vous entendre avec d’autres utilisateurs pour influencer artificiellement les prix, classements ou résultats.",
            ],
          },
          {
            kind: "paragraph",
            text:
              "3.3 Contenu du clavardage. Les messages de clavardage dans les pools sont visibles par tous les membres du pool. Vous êtes seul responsable du contenu que vous publiez. Nous nous réservons le droit de retirer du contenu et de suspendre les comptes qui enfreignent les présentes Conditions, sans préavis.",
          },
        ],
      },
      {
        title: "4. Propriété intellectuelle",
        blocks: [
          {
            kind: "paragraph",
            text:
              "4.1 Notre propriété. L’Application, y compris sa conception, son code, ses éléments graphiques, son interface utilisateur, son moteur de transactions, ses mécaniques de jeu et la marque SportEx100, appartient à SportEx100 Technologies Inc. ou lui est concédée sous licence, et est protégée par les lois relatives au droit d’auteur, aux marques de commerce et à la propriété intellectuelle. Vous ne pouvez pas copier, modifier, distribuer, rétroconcevoir ni créer d’œuvres dérivées de l’Application sans notre consentement écrit préalable.",
          },
          {
            kind: "paragraph",
            text:
              "4.2 Votre contenu. En publiant du contenu dans l’Application, comme des messages de clavardage, codes de trader et avatars, vous nous accordez une licence non exclusive, libre de redevances et mondiale pour utiliser, afficher et distribuer ce contenu dans l’Application aux fins d’exploitation du service. Vous conservez la propriété de votre contenu. Nous ne revendiquons pas la propriété de vos messages de clavardage ni de votre contenu généré par l’utilisateur.",
          },
          {
            kind: "paragraph",
            text:
              "4.3 Commentaires. Si vous nous fournissez des suggestions, idées ou commentaires concernant l’Application, vous acceptez que nous puissions les utiliser librement, sans obligation ni compensation envers vous.",
          },
        ],
      },
      {
        title: "5. Achats intégrés",
        blocks: [
          {
            kind: "paragraph",
            text:
              "L’Application est actuellement gratuite. Nous pourrions introduire à l’avenir des achats intégrés facultatifs afin de débloquer des fonctionnalités améliorées. Si des achats intégrés deviennent disponibles, les présentes Conditions seront mises à jour en conséquence et vous en serez informé à l’avance comme décrit à l’article 7.1.",
          },
        ],
      },
      {
        title: "6. Avis de non-responsabilité et limitation de responsabilité",
        blocks: [
          {
            kind: "paragraph",
            text:
              "6.1 Fourniture telle quelle. L’APPLICATION EST FOURNIE « TELLE QUELLE » ET « SELON LA DISPONIBILITÉ », SANS GARANTIE D’AUCUNE SORTE, EXPRESSE, IMPLICITE OU LÉGALE, Y COMPRIS, SANS S’Y LIMITER, LES GARANTIES IMPLICITES DE QUALITÉ MARCHANDE, D’ADAPTATION À UN USAGE PARTICULIER ET D’ABSENCE DE CONTREFAÇON.",
          },
          {
            kind: "paragraph",
            text:
              "6.2 Aucune garantie. Nous ne garantissons pas que l’Application sera ininterrompue, exempte d’erreurs ou dépourvue de composants nuisibles. Les données de jeu, y compris les portefeuilles, classements, historiques de transactions et prix de marché, peuvent être perdues, corrompues ou réinitialisées à tout moment en raison de problèmes techniques, de mises à jour ou de réinitialisations saisonnières.",
          },
          {
            kind: "paragraph",
            text:
              "6.3 Limitation de responsabilité. DANS LA MESURE MAXIMALE PERMISE PAR LA LOI APPLICABLE, SPORTEX100 TECHNOLOGIES INC., AINSI QUE SES DIRIGEANTS, ADMINISTRATEURS, EMPLOYÉS ET MANDATAIRES, NE SERONT PAS RESPONSABLES DES DOMMAGES INDIRECTS, ACCESSOIRES, SPÉCIAUX, CONSÉCUTIFS OU PUNITIFS, Y COMPRIS, SANS S’Y LIMITER, LA PERTE DE DONNÉES, DE PROFITS OU D’ACHALANDAGE, DÉCOULANT DE VOTRE UTILISATION DE L’APPLICATION OU S’Y RAPPORTANT, QUE CE SOIT SUR LE FONDEMENT D’UNE GARANTIE, D’UN CONTRAT, D’UN DÉLIT CIVIL OU DE TOUTE AUTRE THÉORIE JURIDIQUE.",
          },
          {
            kind: "paragraph",
            text:
              "6.4 Responsabilité maximale. Notre responsabilité globale totale envers vous pour toute réclamation découlant de l’Application ou s’y rapportant ne dépassera en aucun cas le plus élevé des montants suivants : (a) le montant que vous nous avez payé au cours des douze (12) mois précédant la réclamation, ou (b) cinquante dollars canadiens (50,00 $ CA).",
          },
        ],
      },
      {
        title: "7. Modifications et disponibilité du service",
        blocks: [
          {
            kind: "paragraph",
            text:
              "7.1 Modification des Conditions. Nous pouvons modifier les présentes Conditions en tout temps. Si nous apportons des modifications importantes, nous vous en informerons par l’Application ou par tout autre moyen raisonnable au moins quinze (15) jours avant leur prise d’effet. Votre utilisation continue de l’Application après la date d’entrée en vigueur constitue votre acceptation des Conditions mises à jour. Si vous n’acceptez pas les modifications, vous devez cesser d’utiliser l’Application et supprimer votre compte.",
          },
          {
            kind: "paragraph",
            text:
              "7.2 Modifications de l’Application. Nous nous réservons le droit de modifier, suspendre ou interrompre l’Application, ou toute fonctionnalité ou contenu qu’elle contient, en tout temps, avec ou sans préavis. Nous ne serons pas responsables envers vous ni envers un tiers pour toute modification, suspension ou interruption de l’Application.",
          },
          {
            kind: "paragraph",
            text:
              "7.3 Réinitialisations saisonnières. L’Application fonctionne par saisons alignées sur les séries éliminatoires de sports professionnels. À la fin de chaque saison, les portefeuilles, classements, historiques de transactions et données de marché peuvent être réinitialisés. Nous fournirons un préavis raisonnable avant toute réinitialisation saisonnière.",
          },
          {
            kind: "paragraph",
            text:
              "7.4 Disponibilité du service. Nous ne garantissons pas un accès continu ou ininterrompu à l’Application. L’Application peut être temporairement indisponible en raison de maintenance, mises à jour, problèmes de serveur ou circonstances indépendantes de notre volonté.",
          },
        ],
      },
      {
        title: "8. Droit applicable et règlement des différends",
        blocks: [
          {
            kind: "paragraph",
            text:
              "8.1 Droit applicable. Les présentes Conditions sont régies et interprétées conformément aux lois de la province de Québec et aux lois fédérales du Canada qui s’y appliquent, sans égard aux principes de conflit de lois.",
          },
          {
            kind: "paragraph",
            text:
              "8.2 Règlement des différends. Tout différend découlant des présentes Conditions ou de l’Application, ou s’y rapportant, devra d’abord faire l’objet d’une tentative de règlement par négociation de bonne foi. Si le différend ne peut être résolu dans un délai de trente (30) jours, il sera soumis à la compétence exclusive des tribunaux situés dans le district judiciaire de Montréal, Québec, Canada.",
          },
          {
            kind: "paragraph",
            text:
              "8.3 Renonciation aux recours collectifs. Dans toute la mesure permise par la loi applicable, vous acceptez de résoudre les différends avec nous sur une base individuelle et renoncez à tout droit de participer à un recours collectif, une action collective ou une procédure représentative.",
          },
        ],
      },
      {
        title: "9. Dispositions générales",
        blocks: [
          {
            kind: "paragraph",
            text:
              "9.1 Entente intégrale. Les présentes Conditions, avec notre Politique de confidentialité, constituent l’intégralité de l’entente entre vous et nous concernant l’Application et remplacent toutes les ententes et compréhensions antérieures.",
          },
          {
            kind: "paragraph",
            text:
              "9.2 Divisibilité. Si une disposition des présentes Conditions est jugée inapplicable ou invalide, cette disposition sera limitée ou supprimée dans la mesure minimale nécessaire, et les autres dispositions demeureront pleinement en vigueur.",
          },
          {
            kind: "paragraph",
            text:
              "9.3 Renonciation. Le fait que nous n’exercions pas un droit ou n’appliquions pas une disposition des présentes Conditions ne constitue pas une renonciation à ce droit ou à cette disposition.",
          },
          {
            kind: "paragraph",
            text:
              "9.4 Cession. Vous ne pouvez céder ni transférer vos droits en vertu des présentes Conditions sans notre consentement écrit préalable. Nous pouvons céder nos droits et obligations sans restriction.",
          },
        ],
      },
      {
        title: "10. Nous contacter",
        blocks: [
          {
            kind: "paragraph",
            text: "Si vous avez des questions au sujet des présentes Conditions, contactez-nous :",
          },
          {
            kind: "contact",
            lines: [
              "SportEx100 Technologies Inc.",
              "Courriel : info@sportex100.com",
              "Assistance : https://sportex100.com/support",
            ],
          },
        ],
      },
    ],
  },
  ja: {
    eyebrow: "Sport Exchange 100",
    title: "利用規約",
    updated: "発効日：2026年5月16日 - 最終更新日：2026年5月16日",
    intro:
      "本利用規約（以下「本規約」といいます）は、SportEx100 Technologies Inc.（以下「当社」といいます）が運営する Sport Exchange 100 モバイルアプリケーション（以下「本アプリ」といいます）の利用に適用されます。本アプリをダウンロード、インストール、または利用することにより、お客様は本規約に拘束されることに同意したものとみなされます。同意しない場合は、本アプリを利用しないでください。",
    sections: [
      {
        title: "1. 架空通貨およびシミュレーションに関する免責事項",
        blocks: [
          {
            kind: "paragraph",
            text:
              "1.1 現実の価値はありません。Sport Exchange 100 は無料で遊べるシミュレーションゲームです。本アプリ内のすべてのポイント、残高、ランキング、シミュレーションポートフォリオ、取引、注文、予想、マーケット価格は完全に架空のものです。現金価値はなく、入金、出金、償還、金銭との交換、またはいかなる種類の賞品との交換もできません。",
          },
          {
            kind: "paragraph",
            text:
              "1.2 金融活動または賭博活動ではありません。本アプリ内のいかなるものも、実在する資産、契約、証券、商品、賭け、または金融商品を構成または表すものではありません。本アプリは、賭博、ベッティング、実際の金銭取引を促進しません。本アプリの利用により、現実の通貨、暗号資産、または金銭的価値を有するものが賭けられ、勝ち取られ、または失われることはありません。",
          },
          {
            kind: "paragraph",
            text:
              "1.3 非提携。Sport Exchange 100 は、いかなるプロスポーツリーグ、チーム、選手会とも提携しておらず、承認またはスポンサー提供を受けていません。",
          },
        ],
      },
      {
        title: "2. アカウントおよび利用資格",
        blocks: [
          {
            kind: "paragraph",
            text:
              "2.1 年齢要件。アカウントを作成し本アプリを利用するには、13歳以上、またはお住まいの地域でより高いデジタル同意年齢が定められている場合はその年齢以上である必要があります。18歳未満の場合、本アプリの利用について親または保護者の同意を得ていることを表明するものとします。",
          },
          {
            kind: "paragraph",
            text:
              "2.2 アカウント作成。1人につき1つのアカウントを作成できます。アカウントは、Appleでサインイン、Google、またはメール登録により作成できます。お客様は、アカウント認証情報の機密性を維持し、アカウントで行われるすべての活動について責任を負います。",
          },
          {
            kind: "paragraph",
            text:
              "2.3 正確な情報。登録時に正確かつ最新の情報を提供することに同意します。トレーダーコードまたは表示名は、他人になりすますもの、攻撃的な表現を含むもの、または本規約に違反するものであってはなりません。",
          },
          {
            kind: "paragraph",
            text:
              "2.4 アカウント終了。当社は、本規約違反を含む理由の有無を問わず、通知の有無にかかわらず、いつでもお客様のアカウントを停止または終了する権利を留保します。お客様は本アプリの設定からいつでもアカウントを削除できます。削除後、個人データは当社のプライバシーポリシーに従って取り扱われます。",
          },
        ],
      },
      {
        title: "3. ユーザーの行動",
        blocks: [
          {
            kind: "paragraph",
            text: "3.1 一般的な行動。お客様は、本アプリを公正、敬意ある、合法的な方法で利用することに同意します。以下の行為は禁止されています。",
          },
          {
            kind: "list",
            items: [
              "違法な目的、または適用法に違反する目的で本アプリを利用すること。",
              "他のユーザーを嫌がらせ、脅迫、いじめ、威圧すること。",
              "ヘイトスピーチ、差別的表現、性的に露骨な内容、または暴力を助長する内容を投稿または送信すること。",
              "他人になりすますこと、または身元を偽ること。",
              "チャットその他の通信機能でスパム送信、過剰投稿、妨害を行うこと。",
            ],
          },
          {
            kind: "paragraph",
            text: "3.2 ゲームの公正性。以下の行為は禁止されています。",
          },
          {
            kind: "list",
            items: [
              "不公正な優位を得るために複数のアカウントを作成すること。",
              "ボット、スクリプト、自動化ツールを使用して本アプリとやり取りすること。",
              "本アプリのバグ、不具合、脆弱性を悪用すること。発見した場合は当社に報告してください。",
              "ウォッシュトレードやスプーフィングを含む、協調的または欺瞞的な取引活動によりシミュレーション市場を操作すること。",
              "価格、ランキング、結果を人為的に左右するために他のユーザーと共謀すること。",
            ],
          },
          {
            kind: "paragraph",
            text:
              "3.3 チャット内容。プール内のチャットメッセージは、すべてのプールメンバーに表示されます。投稿内容についてはお客様が単独で責任を負います。当社は、本規約に違反するコンテンツを削除し、アカウントを事前通知なく停止する権利を留保します。",
          },
        ],
      },
      {
        title: "4. 知的財産",
        blocks: [
          {
            kind: "paragraph",
            text:
              "4.1 当社の財産。本アプリ（デザイン、コード、グラフィック、ユーザーインターフェース、取引エンジン、ゲームメカニクス、SportEx100 ブランドを含みます）は、SportEx100 Technologies Inc. が所有またはライセンスを受けており、著作権、商標その他の知的財産法により保護されています。当社の事前の書面による同意なく、本アプリをコピー、変更、配布、リバースエンジニアリング、または派生物を作成することはできません。",
          },
          {
            kind: "paragraph",
            text:
              "4.2 お客様のコンテンツ。チャットメッセージ、トレーダーコード、アバターなどのコンテンツを本アプリに投稿することにより、お客様は、サービス運営の目的で本アプリ内において当該コンテンツを使用、表示、配布するための、非独占的、無償、全世界的なライセンスを当社に付与します。お客様はご自身のコンテンツの所有権を保持します。当社は、お客様のチャットメッセージまたはユーザー生成コンテンツの所有権を主張しません。",
          },
          {
            kind: "paragraph",
            text:
              "4.3 フィードバック。お客様が本アプリに関する提案、アイデア、フィードバックを当社に提供した場合、当社はそれらをお客様への義務または補償なしに自由に利用できるものとします。",
          },
        ],
      },
      {
        title: "5. アプリ内課金",
        blocks: [
          {
            kind: "paragraph",
            text:
              "本アプリは現在無料で利用できます。将来的に、拡張機能または追加機能を有効にするための任意のアプリ内課金を導入する場合があります。アプリ内課金が利用可能になる場合、本規約はそれに応じて更新され、第7.1条に記載された方法で事前に通知されます。",
          },
        ],
      },
      {
        title: "6. 免責事項および責任制限",
        blocks: [
          {
            kind: "paragraph",
            text:
              "6.1 現状有姿。本アプリは「現状有姿」かつ「提供可能な範囲」で提供され、明示、黙示、法定を問わず、商品性、特定目的への適合性、非侵害の黙示保証を含むがこれらに限定されない、いかなる種類の保証もありません。",
          },
          {
            kind: "paragraph",
            text:
              "6.2 保証なし。当社は、本アプリが中断なく、エラーなく、または有害な要素なしに利用できることを保証しません。ポートフォリオ、ランキング、取引履歴、マーケット価格などのゲームデータは、技術的問題、更新、季節ごとのリセットにより、いつでも失われ、破損し、またはリセットされることがあります。",
          },
          {
            kind: "paragraph",
            text:
              "6.3 責任制限。適用法で認められる最大限の範囲において、SPORTEX100 TECHNOLOGIES INC. およびその役員、取締役、従業員、代理人は、保証、契約、不法行為その他いかなる法理に基づくかを問わず、本アプリの利用に起因または関連して生じる、データ損失、利益損失、信用損失を含むがこれらに限定されない、間接的、付随的、特別、結果的、懲罰的損害について責任を負いません。",
          },
          {
            kind: "paragraph",
            text:
              "6.4 最大責任額。本アプリに起因または関連するすべての請求に対する当社の総責任額は、いかなる場合も、(a) 請求前12か月間にお客様が当社に支払った金額、または (b) 50カナダドル（CAD $50.00）のいずれか高い方を超えないものとします。",
          },
        ],
      },
      {
        title: "7. 変更およびサービス提供",
        blocks: [
          {
            kind: "paragraph",
            text:
              "7.1 規約の変更。当社はいつでも本規約を変更できます。重大な変更を行う場合、変更の効力発生日の少なくとも15日前に、本アプリまたはその他の合理的な方法で通知します。効力発生日後に本アプリを継続して利用することにより、更新後の規約に同意したものとみなされます。変更に同意しない場合は、本アプリの利用を中止し、アカウントを削除してください。",
          },
          {
            kind: "paragraph",
            text:
              "7.2 本アプリの変更。当社は、本アプリまたはその機能・コンテンツを、通知の有無にかかわらず、いつでも変更、停止、中止する権利を留保します。当社は、本アプリの変更、停止、中止について、お客様または第三者に対して責任を負いません。",
          },
          {
            kind: "paragraph",
            text:
              "7.3 季節ごとのリセット。本アプリは、プロスポーツのプレーオフに合わせたシーズン制で運営されます。各シーズン終了時に、ポートフォリオ、ランキング、取引履歴、マーケットデータがリセットされる場合があります。季節ごとのリセット前には合理的な通知を行います。",
          },
          {
            kind: "paragraph",
            text:
              "7.4 サービス提供。本アプリへの継続的または中断のないアクセスを保証するものではありません。本アプリは、メンテナンス、更新、サーバー問題、または当社の管理外の事情により、一時的に利用できなくなる場合があります。",
          },
        ],
      },
      {
        title: "8. 準拠法および紛争解決",
        blocks: [
          {
            kind: "paragraph",
            text:
              "8.1 準拠法。本規約は、抵触法の原則にかかわらず、ケベック州法および同州に適用されるカナダ連邦法に準拠し、これに従って解釈されます。",
          },
          {
            kind: "paragraph",
            text:
              "8.2 紛争解決。本規約または本アプリに起因または関連する紛争は、まず誠実な交渉により解決を試みるものとします。30日以内に解決できない場合、当該紛争はカナダ・ケベック州モントリオール司法地区に所在する裁判所の専属管轄に服するものとします。",
          },
          {
            kind: "paragraph",
            text:
              "8.3 集団訴訟の放棄。適用法で認められる最大限の範囲において、お客様は当社との紛争を個別に解決することに同意し、集団訴訟、集合訴訟、代表訴訟に参加する権利を放棄します。",
          },
        ],
      },
      {
        title: "9. 一般条項",
        blocks: [
          {
            kind: "paragraph",
            text:
              "9.1 完全合意。本規約および当社のプライバシーポリシーは、本アプリに関するお客様と当社との完全な合意を構成し、従前のすべての合意および理解に優先します。",
          },
          {
            kind: "paragraph",
            text:
              "9.2 分離可能性。本規約のいずれかの条項が執行不能または無効と判断された場合、その条項は必要最小限の範囲で制限または削除され、残りの条項は引き続き完全に有効に存続します。",
          },
          {
            kind: "paragraph",
            text:
              "9.3 権利不放棄。当社が本規約上の権利または条項を行使または執行しなかった場合でも、当該権利または条項を放棄したものとはみなされません。",
          },
          {
            kind: "paragraph",
            text:
              "9.4 譲渡。お客様は、当社の事前の書面による同意なく、本規約上の権利を譲渡または移転することはできません。当社は、当社の権利および義務を制限なく譲渡できます。",
          },
        ],
      },
      {
        title: "10. お問い合わせ",
        blocks: [
          {
            kind: "paragraph",
            text: "本規約についてご質問がある場合は、以下までお問い合わせください。",
          },
          {
            kind: "contact",
            lines: [
              "SportEx100 Technologies Inc.",
              "メール：info@sportex100.com",
              "サポート：https://sportex100.com/support",
            ],
          },
        ],
      },
    ],
  },
};

function renderBlock(block: TermsSection["blocks"][number], index: number) {
  if (block.kind === "list") {
    return (
      <ul key={index} className="mt-2 list-disc space-y-1 pl-5">
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }

  if (block.kind === "contact") {
    return (
      <div key={index} className="mt-2 space-y-1 font-semibold text-foreground">
        {block.lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    );
  }

  return (
    <p key={index} className="mt-2">
      {block.text}
    </p>
  );
}

export function TermsPage() {
  const { locale } = useLocalization();
  const content = termsContent[locale] ?? termsContent.en;
  if (!content) return null;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-4 md:gap-6 md:px-0">
      <section className="rounded-card border border-border-subtle bg-background-card p-4 shadow-card md:p-6">
        <p className="text-label-sm font-semibold uppercase text-accent">{content.eyebrow}</p>
        <h1 className="mt-1 text-title-sm font-bold tracking-tight text-foreground md:text-title-md">
          {content.title}
        </h1>
        <p className="mt-2 text-body-sm text-foreground-muted">{content.updated}</p>
      </section>

      <section className="rounded-card border border-border-subtle bg-background-card p-4 shadow-card md:p-6">
        <div className="space-y-5 text-body-sm text-foreground">
          <p>{content.intro}</p>

          {content.sections.map((section) => (
            <div key={section.title}>
              <h2 className="text-title-xs font-semibold text-foreground">{section.title}</h2>
              {section.blocks.map(renderBlock)}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
